import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';

import * as S from './styles';

import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import BlockIcon from '@mui/icons-material/Block';

import { IExamAnswerSheet } from '../../../..';
import { AlertBar, AlertWarning } from '../../../../../../Alert';
import TableBasic, { ITableData } from '../../../../../../Table';
import {
  cleanTrailingEmptyParagraph,
  formatDateHour,
  numberToString,
} from '../../../../../../../../util/common/appUtil';
import { ButtonSimple } from '../../../../../../Buttons';
import AccessExamReview from '../../../../../AccessExamReview';
import FieldFormik from '../../../../../../FieldFormik';
import {
  INewStudentProvider,
  NewStudentProviderContext,
} from '../../../../../../ProviderNewStudent';
import QuillEditor from '../../../../../../InputTextQuill';
import AlertExamMinimumChar from '../../../../../AlertExamMinimumChar';
import { handleMaxWidth } from '../../../../../../../../util/newStudent';
import { useTheme } from 'styled-components';
import { CardLeftColor } from '../../../../../../Card';
import {
  IAlternative,
  IAnswerQuestion,
  IAnswers,
  IExam,
  IQuestion,
  myCoursesServiceNewStudent,
} from '../../../../../../../../services/newStudent/myCourses/myCourses.service';
import {
  typeExam,
  typeExamMultipleChoice,
  typeExamRecovery,
  typeExamSubjective,
} from '../../../../../../../../variables';

interface Props {
  data?: IExam;
  setData?: Dispatch<SetStateAction<IExam | undefined>>;
  setContentSelected: Dispatch<SetStateAction<string>>;
  setBlockedContent: Dispatch<SetStateAction<boolean>>;
}

const renderAnswerResult = (item: Pick<IQuestion, 'result' | 'isAnnulled'>) => {
  if (item.isAnnulled) {
    return (
      <S.DivIsCorrect isAnnulled>
        <BlockIcon fontSize="large" />
        Questão anulada
      </S.DivIsCorrect>
    );
  }

  return (
    <S.DivIsCorrect isCorrect={item.result}>
      {item.result ? (
        <CheckIcon fontSize="large" />
      ) : (
        <CloseIcon fontSize="large" />
      )}
      Resposta {item.result ? 'correta' : 'incorreta'}
    </S.DivIsCorrect>
  );
};

export default function ExamContent({
  data,
  setData,
  setContentSelected,
  setBlockedContent,
}: Props) {
  const { setIsLoading } = useContext(
    NewStudentProviderContext,
  ) as INewStudentProvider;
  const defaultQuestion = {
    examType: 0,
    answers: [{ questionId: 0, alternativeId: null }],
  };

  // const [tmpResponse, setTmpResponse] =
  //   useState<IAnswerQuestion>(defaultQuestion);
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);

  const [questions, setQuestions] = useState<IAnswerQuestion>(defaultQuestion);
  const [inReview, setInReview] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const [modalIsReviewedOpen, setModalIsReviewedOpen] = useState(false);
  const [modalIsAtentionOpen, setModalIsAtentionOpen] = useState(false);

  const [countText, setCountText] = useState(0);

  const [recoveryExamText, setRecoveryExamText] = useState<
    Record<number, string>
  >({});
  const minimumChar = 2600;

  const [dataTable, setDataTable] = useState<ITableData>({
    title: [],
    content: [],
  });
  const [dataTableMobile, setDataTableMobile] = useState<IExamAnswerSheet[]>();
  const [questionsOpen, setQuestionsOpen] = useState<IQuestion[]>();
  const theme = useTheme();

  const handleRecoveryExamTextChange = (questionId: number, text: string) => {
    setRecoveryExamText((prev) => ({
      ...prev,
      [questionId]: text,
    }));
  };

  /** Roda as informações para mostrar o gabarito  */
  useEffect(() => {
    setIsLoading(true);
    setInReview(false);
    setReviewed(false);
    setModalIsReviewedOpen(false);
    setModalIsAtentionOpen(false);
    setCountText(0);
    setRecoveryExamText('');

    if (
      data?.examType === +typeExamSubjective! &&
      data?.examQuestions[0].answer
    ) {
      handleRecoveryExamTextChange(
        data?.examQuestions[0].questionId,
        String(data?.examQuestions[0].answer),
      );
    }

    if (
      data?.examQuestions[0].teacherObservation &&
      data?.examQuestions[0].response
    ) {
      handleRecoveryExamTextChange(
        data?.examQuestions[0].questionId,
        String(data?.examQuestions[0].response),
      );
    }

    if (data?.examType === +typeExamMultipleChoice! && data?.examId) {
      const answers = {
        examType: 0,
        answers:
          data?.examQuestions
            .flatMap((p) =>
              p.alternatives.map((alternative) => ({
                ...alternative,
                questionId: p.questionId,
              })),
            )
            .filter((filtered) => filtered.response)
            .map((mapped) => ({
              alternativeId: mapped.alternativeId,
              questionId: mapped.questionId,
            })) ?? [],
      };

      setQuestions(answers);

      // 🔹 força o save inicial pra registrar a tentativa no backend
      const initSave = async () => {
        try {
          await myCoursesServiceNewStudent.answerQuestion({
            examType: data?.examType,
            answers: [],
          });
        } catch (e) {
          throw new Error(String(e));
        }
      };

      initSave();
    }

    if (
      data?.examType === +typeExam! ||
      data?.examType === +typeExamRecovery!
    ) {
      const answers = data?.examQuestions?.map((item: IQuestion) => {
        if (item.alternatives?.length) {
          const selected = item.alternatives.find(
            (alternative: IAlternative) => alternative.response === true,
          );

          return {
            questionId: item.questionId,
            alternativeId: selected?.alternativeId,
          };
        } else {
          const text = item.answer ?? item.response;
          handleRecoveryExamTextChange(item.questionId, text);

          return {
            questionId: item.questionId,
            alternativeText: recoveryExamText[item.questionId] || '',
          };
        }
      });

      setQuestions({
        examType: data?.examType,
        answers: answers,
      });
    }

    try {
      if (data?.title?.trim().toLowerCase().includes('finali')) {
        const questionsOpen: IQuestion[] = [];
        const questionsClose: IQuestion[] = [];

        data?.examQuestions?.forEach((item) => {
          if (item.type == 1) {
            questionsOpen.push(item);
          } else {
            questionsClose.push(item);
          }
        });

        setQuestionsOpen(questionsOpen);

        setDataTable({
          title: [
            { item: <S.TitleTable>Questão</S.TitleTable>, align: 'center' },
            { item: <S.TitleTable>Resposta</S.TitleTable>, align: 'center' },
            { item: <S.TitleTable>Resultado</S.TitleTable>, align: 'center' },
            {
              item: <S.TitleTable>Respondido em</S.TitleTable>,
              align: 'center',
            },
          ],
          content: questionsClose?.map((item) => ({
            question: {
              item: String(item.number).padStart(2, '0'),
              align: 'center',
            },
            response: {
              item: (
                <div dangerouslySetInnerHTML={{ __html: item.response }}></div>
              ),
              align: 'center',
              style: { maxWidth: '200px' },
            },
            isCorrect: {
              item: renderAnswerResult(item),
              align: 'center',
            },
            responseDate: {
              item: formatDateHour(item.date),
              align: 'center',
            },
          })),
        });

        setDataTableMobile(
          questionsClose?.map((item) => ({
            number: item.number,
            response: item.response,
            result: item.result,
            isAnnulled: item.isAnnulled,
            date: item.date,
          })),
        );
      }
    } catch (error) {
      throw new Error(error);
    }

    setIsLoading(false);
  }, [data, data?.title]);

  /** Fetch para salvar a prova e traz o resultado das questões */
  const handleSubmitExam = async () => {
    setIsLoading(true);
    try {
      const { result } = await myCoursesServiceNewStudent.finishExam(
        data?.examId,
        data?.examType,
      );

      if (result) {
        setBlockedContent(false);
        setContentSelected('400');
        setTimeout(() => {
          setBlockedContent(false);
          setContentSelected('3');
        }, 1000);

        setData && setData(undefined);
        setQuestions(defaultQuestion);
        setInReview(false);
        setReviewed(false);
      }
    } catch (error) {
      throw new Error(error);
    }
    setIsLoading(false);
  };

  /**
   * Faz a requisição de tempo em tempo para guardar a resposta do usuário
   * para que ele não perca a prova se der algum erro no computador, prova, internet, etc
   */
  const handleSaveAnswers = () => {
    if (data?.response) return;

    if (timer) clearTimeout(timer);

    const newTimer = setTimeout(async () => {
      try {
        if (data?.examType === +typeExamSubjective!) {
          await myCoursesServiceNewStudent.answerQuestion({
            examType: data?.examType,
            answers: data.examQuestions.map((q) => ({
              questionId: q.questionId,
              alternativeText: recoveryExamText[q.questionId],
            })),
          });
        }

        if (
          data?.examType === +typeExam! ||
          data?.examType === +typeExamRecovery!
        ) {
          const answers = questions?.answers?.map((item: IAnswers) => {
            if (item.alternativeId !== undefined) return item;
            return {
              ...item,
              alternativeText: recoveryExamText[item.questionId] || '',
            };
          });

          await myCoursesServiceNewStudent.answerQuestion({
            examType: data?.examType,
            answers,
          });
        }
      } catch (error) {
        throw new Error(String(error));
      }
    }, 600);

    setTimer(newTimer);
  };

  const handleDebouncedSaveAnswers = (questionId: number, text: string) => {
    handleRecoveryExamTextChange(questionId, text);

    if (timer) clearTimeout(timer);

    const newTimer = setTimeout(async () => {
      if (!data?.examQuestions?.length) return;

      try {
        await myCoursesServiceNewStudent.answerQuestion({
          examType: data?.examType,
          answers: data.examQuestions.map((q) => ({
            questionId: q.questionId,
            alternativeText: recoveryExamText[q.questionId] || '',
          })),
        });
      } catch (error) {
        throw new Error(String(error));
      }
    }, 800);

    setTimer(newTimer);
  };

  /** Função para guardar as resposta das questões */
  const handleAddResponseQuestion = async (
    questionId: number,
    alternativeId: number | null,
  ) => {
    const updateAnswers = (oldAnswers, questionId, alternativeId) => {
      const filteredAnswers = oldAnswers.filter(
        (answer) =>
          answer.questionId !== questionId &&
          answer.questionId !== 0 &&
          answer.questionId !== undefined,
      );

      return [...filteredAnswers, { questionId, alternativeId }];
    };

    const updatedAnswers = updateAnswers(
      questions.answers,
      questionId,
      alternativeId,
    );

    await myCoursesServiceNewStudent.answerQuestion({
      examType: data?.examType ?? 0,
      answers: [{ questionId, alternativeId }],
    });

    setQuestions({
      examType: data?.examType ?? 0,
      answers: updatedAnswers,
    });
  };

  const validateQuestionFilter = (item: IQuestion) =>
    questions.answers.filter((idAnswer: IAnswers) => {
      return idAnswer?.questionId === item?.questionId;
    });

  const validateQuestion = (item, res) => {
    const isSelected =
      validateQuestionFilter(item)[0]?.alternativeId === res.alternativeId;

    const newAlternativeId = isSelected ? null : res.alternativeId;

    handleAddResponseQuestion(item.questionId, newAlternativeId);
  };

  const getGabaritoMobile = (data) => {
    return data?.map((item, index) => (
      <CardLeftColor
        key={index}
        color={
          item.isAnnulled ? 'warning' : item.result ? 'success' : 'danger'
        }
        heightSize="fullHeight"
      >
        <S.DivKeyValue>
          <S.KeyValue>
            <S.Key>Questão: </S.Key>
            <S.Value>{item.number}</S.Value>
          </S.KeyValue>
          <S.KeyValue>
            <S.Key>Resposta: </S.Key>
            <S.Value>{item.response}</S.Value>
          </S.KeyValue>
          <S.KeyValue>
            <S.Key>Respondido em: </S.Key>
            <S.Value>{formatDateHour(item.date)}</S.Value>
          </S.KeyValue>
          <S.Result
            color={
              item.isAnnulled ? 'warning' : item.result ? 'success' : 'danger'
            }
          >
            {item.isAnnulled
              ? 'Questão anulada'
              : item.result
                ? 'Correto'
                : 'Incorreto'}
          </S.Result>
        </S.DivKeyValue>
      </CardLeftColor>
    ));
  };

  const getGabarito = (data) => {
    return (
      <S.Content key={data?.title}>
        <S.Div>
          <S.DivName>
            <S.H4>Gabarito</S.H4>
          </S.DivName>
          <S.DivName>
            <S.H6>{data?.title}</S.H6>
          </S.DivName>
          <S.DivName>
            <S.H6>Situação: {data.gradeStatus}</S.H6>
          </S.DivName>
          <S.DivName>
            <S.H6>Nota: {numberToString(String(data.grade))}</S.H6>
          </S.DivName>
        </S.Div>

        {data.observation && (
          <AlertBar color={'danger'} text={data.observation} noWarning />
        )}

        <TableBasic data={dataTable} key={'table-basic'} />

        {questionsOpen &&
          questionsOpen.length > 0 &&
          questionsOpen.map((item, index) => (
            <S.QuestionDiv key={index} style={{ paddingBottom: '2rem' }}>
              <div>
                <S.H6Question inReview={inReview}>
                  Questão {String(item.number).padStart(2, '0')}
                </S.H6Question>
                {item.isAnnulled && renderAnswerResult(item)}
              </div>
              <QuillEditor
                index={item.questionId}
                text={recoveryExamText[item.questionId] || ''}
                setText={(text: string) =>
                  handleDebouncedSaveAnswers(item.questionId, text)
                }
                disabled={inReview || !!item.teacherObservation}
                setCountChar={setCountText}
              />
              {item.teacherObservation && (
                <>
                  <div>
                    <S.H6Question inReview={inReview}>
                      Observação do Professor
                    </S.H6Question>

                    {!item.isAnnulled &&
                      item.result &&
                      renderAnswerResult(item)}
                  </div>
                  <S.H6Response>{item.teacherObservation}</S.H6Response>
                </>
              )}
            </S.QuestionDiv>
          ))}
      </S.Content>
    );
  };

  const getExamGraduation = (data) => {
    return (
      <S.Content>
        <S.Div key={data?.title}>
          <S.DivName>
            <S.H4>Avaliação</S.H4>
          </S.DivName>
          <S.DivName>
            <S.H6>{data?.title}</S.H6>
          </S.DivName>
        </S.Div>
        {data?.examQuestions?.map((item: IQuestion, index: number) => (
          <S.QuestionDiv key={index}>
            <div>
              <S.H6Question inReview={inReview}>
                Questão {index + 1}
              </S.H6Question>
              <S.H6Question inReview={inReview}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: cleanTrailingEmptyParagraph(item.question),
                  }}
                ></div>
              </S.H6Question>
              {item.url && <S.ImgQuestion src={item.url} />}
            </div>
            <div>
              {item.alternatives?.length > 0
                ? item.alternatives.map((res, key) => (
                    <S.DivResponse key={key} className={'question_' + key}>
                      <div style={{ height: '2rem' }}>
                        <input
                          type="radio"
                          style={{
                            cursor: !inReview ? 'pointer' : 'default',
                          }}
                          disabled={inReview}
                          onClick={() =>
                            !inReview && validateQuestion(item, res)
                          }
                          checked={
                            validateQuestionFilter(item)[0]?.alternativeId ===
                            res.alternativeId
                          }
                        />
                      </div>
                      <S.H6Response
                        onClick={() => !inReview && validateQuestion(item, res)}
                      >
                        <div
                          style={{
                            color:
                              validateQuestionFilter(item)[0]?.alternativeId ===
                                res.alternativeId && inReview
                                ? theme.colors?.text.info01
                                : '',
                          }}
                          dangerouslySetInnerHTML={{
                            __html: res.alternative + ') ' + res.text,
                          }}
                        ></div>
                      </S.H6Response>
                    </S.DivResponse>
                  ))
                : item.questionId && (
                    <>
                      <QuillEditor
                        index={item.questionId}
                        text={recoveryExamText[item.questionId] || ''}
                        setText={(text: string) =>
                          handleDebouncedSaveAnswers(item.questionId, text)
                        }
                        disabled={inReview || !!item.teacherObservation}
                      />
                    </>
                  )}
            </div>
          </S.QuestionDiv>
        ))}
        {inReview ? (
          <>
            <S.Buttons>
              <ButtonSimple
                color="danger"
                widthSize="large"
                onClick={() => {
                  setReviewed(false);
                  setInReview(false);
                }}
              >
                Corrigir
              </ButtonSimple>
              <ButtonSimple
                color="success"
                widthSize="large"
                onClick={() => handleSubmitExam()}
                disabled={!reviewed}
              >
                Finalizar
              </ButtonSimple>
            </S.Buttons>
            <S.FieldDiv>
              <FieldFormik
                name="readed"
                placeholder="
                Estou ciente que após a finalização da prova não será possível responder ou alterar a prova novamente."
                onChange={(e) => {
                  setReviewed(e.target.checked);
                }}
                type="checkbox"
                checked={!!reviewed}
                widthSize="fullWidth"
              />
            </S.FieldDiv>
          </>
        ) : (
          <>
            <S.ButtonExam>
              <ButtonSimple
                color="success"
                widthSize="large"
                onClick={() => {
                  setModalIsReviewedOpen(true);
                }}
              >
                Confirmar
              </ButtonSimple>
            </S.ButtonExam>
            <AlertWarning title="Após confirmação,você será direcionado para a página de revisão" />
          </>
        )}
      </S.Content>
    );
  };

  const getExamMultipleChoice = (data) => {
    return (
      <S.Content>
        <S.Div key={data?.title}>
          <S.DivName>
            <S.H4>Avaliação</S.H4>
          </S.DivName>
          <S.DivName>
            <S.H6>{data?.title}</S.H6>
          </S.DivName>
        </S.Div>
        {data?.examQuestions?.map((item: IQuestion, index: number) => (
          <S.QuestionDiv key={index}>
            <>
              <S.H6Question inReview={inReview}>
                Questão {index + 1}
              </S.H6Question>
              <S.H6Question inReview={inReview}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: cleanTrailingEmptyParagraph(item.question),
                  }}
                ></div>
              </S.H6Question>
              {item.url && <S.ImgQuestion src={item.url} />}
            </>
            <div>
              {item.alternatives.map((res, key) => (
                <S.DivResponse key={key} className={'question_' + key}>
                  <div style={{ height: '2rem' }}>
                    <input
                      type="radio"
                      style={{ cursor: !inReview ? 'pointer' : 'default' }}
                      disabled={inReview}
                      onClick={() => !inReview && validateQuestion(item, res)}
                      checked={
                        validateQuestionFilter(item)[0]?.alternativeId ===
                        res.alternativeId
                      }
                    />
                  </div>
                  <S.H6Response
                    onClick={() => !inReview && validateQuestion(item, res)}
                  >
                    <div
                      style={{
                        color:
                          validateQuestionFilter(item)[0]?.alternativeId ===
                            res.alternativeId && inReview
                            ? theme.colors?.text.info01
                            : '',
                      }}
                      dangerouslySetInnerHTML={{
                        __html: res.alternative + ') ' + res.text,
                      }}
                    ></div>
                  </S.H6Response>
                </S.DivResponse>
              ))}
            </div>
          </S.QuestionDiv>
        ))}
        {inReview ? (
          <>
            <S.Buttons>
              <ButtonSimple
                color="danger"
                widthSize="large"
                onClick={() => {
                  setReviewed(false);
                  setInReview(false);
                }}
              >
                Corrigir
              </ButtonSimple>
              <ButtonSimple
                color="success"
                widthSize="large"
                onClick={() => handleSubmitExam()}
                disabled={!reviewed}
              >
                Finalizar
              </ButtonSimple>
            </S.Buttons>
            <S.FieldDiv>
              <FieldFormik
                name="readed"
                placeholder="
                Estou ciente que após a finalização da prova não será possível responder ou alterar a prova novamente."
                onChange={(e) => {
                  setReviewed(e.target.checked);
                }}
                type="checkbox"
                checked={!!reviewed}
                widthSize="fullWidth"
              />
            </S.FieldDiv>
          </>
        ) : (
          <>
            <S.ButtonExam>
              <ButtonSimple
                color="success"
                widthSize="large"
                onClick={() => {
                  setModalIsReviewedOpen(true);
                }}
              >
                Confirmar
              </ButtonSimple>
            </S.ButtonExam>
            <AlertWarning title="Após confirmação,você será direcionado para a página de revisão" />
          </>
        )}
      </S.Content>
    );
  };

  const getExamSubjective = (data) => {
    return (
      <S.Content>
        <S.Div key={data?.title}>
          <S.DivName>
            <S.H4>Avaliação</S.H4>
          </S.DivName>
          <S.DivName>
            <S.H6>{data?.title}</S.H6>
          </S.DivName>
        </S.Div>
        {data.observation ? (
          <AlertBar color={'danger'} text={data.observation} noWarning />
        ) : (
          <></>
        )}
        {data?.examQuestions?.map((item, index) => (
          <S.QuestionDiv key={index} style={{ paddingBottom: '2rem' }}>
            <div>
              <S.H6Question inReview={inReview}>
                Questão {index + 1}
              </S.H6Question>
              <S.H6Question inReview={inReview}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: cleanTrailingEmptyParagraph(item.question),
                  }}
                ></div>
              </S.H6Question>
              {item.url && <S.ImgQuestion src={item.url} />}
            </div>
            {countText > 0 && item?.question?.includes('lauda') && (
              <S.CountChar>
                Quantidade de caracteres digitados: {countText}
              </S.CountChar>
            )}
            {
              <QuillEditor
                index={item.questionId}
                text={recoveryExamText[item.questionId] || ''}
                setText={(text: string) =>
                  handleDebouncedSaveAnswers(item.questionId, text)
                }
                disabled={inReview || !!item.teacherObservation}
                setCountChar={setCountText}
              />
            }
          </S.QuestionDiv>
        ))}
        {inReview ? (
          <>
            <S.Buttons>
              <ButtonSimple
                color="danger"
                widthSize="large"
                onClick={() => {
                  setReviewed(false);
                  setInReview(false);
                }}
              >
                Corrigir
              </ButtonSimple>
              <ButtonSimple
                color="success"
                widthSize="large"
                onClick={() => handleSubmitExam()}
                disabled={!reviewed}
              >
                Finalizar
              </ButtonSimple>
            </S.Buttons>
            <S.FieldDiv>
              <FieldFormik
                name="readed"
                placeholder="
                Estou ciente que após a finalização da prova não será possível responder ou alterar a prova novamente."
                onChange={(e) => {
                  setReviewed(e.target.checked);
                }}
                type="checkbox"
                checked={!!reviewed}
                widthSize="fullWidth"
              />
            </S.FieldDiv>
          </>
        ) : (
          !data?.title?.trim().toLowerCase().includes('finalizada') && (
            <>
              <S.Button>
                <ButtonSimple
                  color="success"
                  widthSize="large"
                  onClick={() => {
                    handleSaveAnswers();
                    if (countText < minimumChar && countText > 0) {
                      setModalIsAtentionOpen(true);
                    } else {
                      setModalIsReviewedOpen(true);
                    }
                  }}
                >
                  Confirmar
                </ButtonSimple>
              </S.Button>
              <AlertWarning title="Após confirmação,você será direcionado para a página de revisão" />
            </>
          )
        )}
        <AlertExamMinimumChar
          count={countText}
          isOpen={modalIsAtentionOpen}
          setIsOpen={setModalIsAtentionOpen}
          minimum={minimumChar}
        />
      </S.Content>
    );
  };

  return (
    <S.Container className="exam_content">
      {data?.title?.trim().toLowerCase().includes('finalizada') &&
        !data?.examQuestions[0].teacherObservation &&
        (handleMaxWidth(theme.size?.medium)
          ? getGabaritoMobile(dataTableMobile)
          : getGabarito(data))}
      {(data?.examType === +typeExamRecovery! ||
        data?.examType === +typeExam!) &&
        data?.examId &&
        getExamGraduation(data)}
      {data?.examType === +typeExamMultipleChoice! &&
        data?.examId &&
        getExamMultipleChoice(data)}
      {(data?.examType === +typeExamSubjective! ||
        data?.examQuestions[0].teacherObservation) &&
        getExamSubjective(data)}
      {(data?.examType === +typeExamSubjective! ||
        data?.examType === +typeExamMultipleChoice! ||
        data?.examType === +typeExam! ||
        data?.examType === +typeExamRecovery!) && (
        <>
          <AccessExamReview
            isOpen={modalIsReviewedOpen}
            setIsOpen={setModalIsReviewedOpen}
            setInReview={setInReview}
          />
        </>
      )}
    </S.Container>
  );
}
